import { MessageSquareText } from "lucide-react";
import { useEffect, useState } from "react";

import BlocoRecolhivel from "./BlocoRecolhivel";
import Chave from "./Chave";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}`, "Content-Type": "application/json" });

// CLEO anota pedidos pela conversa (pedido do dono, 04/10/2026): o cliente diz
// o que quer no WhatsApp e a CLEO monta o pedido como um atendente (produto,
// endereço, pagamento, resumo, confirmação). Nasce ligado em toda loja; esta
// chave desliga, e o pedido novo volta a ser só pelo link do cardápio.
// Grava em /api/chatbot/cleo-config (atendimento.anotar_pedido).
// Segunda chave, dentro do bloco (fase 5 do plano): "CLEO oferece um item a mais"
// (atendimento.oferecer_no_pedido) — no meio do pedido ela oferece UMA coisa
// (combo, borda, adicional do item). Nasce ligada; só vale com a de cima ligada. OUTRO produto pra acompanhar
// (cross-sell) ficou fora por decisão do dono (16/10/2026): `oferta_outro.py`, sem chave nesta tela.
export default function CleoPedidosPreferencias({ toast }) {
  const [ligado, setLigado] = useState(null); // null = carregando
  const [atendimento, setAtendimento] = useState(true);
  const [oferece, setOferece] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/api/chatbot/cleo-config`, { headers: auth(), cache: "no-store" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        const cfg = d.config || {};
        setLigado(cfg.atendimento?.anotar_pedido !== false);
        setOferece(cfg.atendimento?.oferecer_no_pedido !== false);
        setAtendimento(cfg.ativa !== false && cfg.atendimento?.ativo !== false);
      })
      .catch(() => setLigado(true));
  }, []);

  const alternar = async () => {
    const novo = !ligado;
    setLigado(novo);
    try {
      const r = await fetch(`${API_URL}/api/chatbot/cleo-config`, {
        method: "PUT", headers: auth(), body: JSON.stringify({ config: { atendimento: { anotar_pedido: novo } } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Não foi possível salvar.");
      toast({
        title: novo ? "CLEO anotando pedidos" : "CLEO não anota mais pedidos",
        description: novo
          ? "Quando o cliente disser o que quer no WhatsApp, a CLEO anota e o pedido entra no sistema depois que ele confirmar."
          : "Pedido novo volta a ser só pelo link do cardápio. A CLEO continua tirando dúvidas.",
      });
    } catch (e) {
      setLigado(!novo);
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  };

  const alternarOferta = async () => {
    const novo = !oferece;
    setOferece(novo);
    try {
      const r = await fetch(`${API_URL}/api/chatbot/cleo-config`, {
        method: "PUT", headers: auth(), body: JSON.stringify({ config: { atendimento: { oferecer_no_pedido: novo } } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Não foi possível salvar.");
      toast({
        title: novo ? "CLEO oferecendo um item a mais" : "CLEO não oferece mais",
        description: novo
          ? "No meio do pedido ela oferece uma coisa: o combo, a borda ou um adicional do próprio item. Uma vez só, sem insistir."
          : "Ela volta a anotar só o que o cliente pedir.",
      });
    } catch (e) {
      setOferece(!novo);
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  };

  const resumo = ligado === null ? "Carregando…"
    : !ligado ? "Desligado — pedido novo só pelo link do cardápio"
    : !atendimento ? "Ligado, mas sem efeito: o Atendimento da CLEO está desligado nesta loja"
    : "Ligado — o cliente pede pelo WhatsApp e a CLEO anota";

  return (
    <BlocoRecolhivel e2e="card-cleo-pedidos" icone={MessageSquareText} titulo="CLEO anota pedidos pela conversa" resumo={resumo} carregando={ligado === null}
      controle={<Chave ligado={!!ligado} onAlternar={alternar} desabilitado={ligado === null} id="sw-cleo-pedidos" />}>
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          O cliente escreve o que quer (<em>“quero um cheese bacon”</em>) e a CLEO anota: confirma o produto pelo nome do
          cadastro, pergunta o endereço e a forma de pagamento, manda o resumo com o total e só cria o pedido depois do
          <strong> sim</strong> do cliente. O pedido entra como os do cardápio (imprime, vai pra cozinha e pro caixa) e aparece
          com o selo <strong>CLEO</strong> em Vendas → Pedidos.
        </p>
        <p>
          Pizza ela anota com tamanho, meio a
          meio e borda; produto por quilo, com o peso. O cupom visível da loja e a oferta do dia do cliente entram no
          total, como no cardápio. Com a loja fechada ela só anota se Agendamentos estiver ligado: o pedido fica
          agendado. As formas de pagamento são as marcadas para o WhatsApp em
          Financeiro → Formas de Pagamento; no PIX Online o código é mandado pelo sistema.
        </p>
        <div className="flex items-start justify-between gap-3 rounded-lg border p-3" data-e2e="linha-cleo-oferece">
          <div>
            <p className="font-medium text-foreground">CLEO oferece um item a mais</p>
            <p>
              Depois de anotar, ela faz <strong>uma</strong> oferta por pedido, com o preço: o lanche no combo, a borda
              recheada da pizza ou o adicional mais pedido do item. Ela não sugere outro produto. O cliente responde sim
              ou não e ela segue, sem insistir. Desligada, ela anota só o que o cliente pedir.
            </p>
          </div>
          <Chave ligado={!!oferece && !!ligado} onAlternar={alternarOferta} desabilitado={ligado === null || !ligado} id="sw-cleo-oferece" />
        </div>
        <p>O quanto ela vendeu, e o que as ofertas somaram, fica em ChatBot → Vendas da CLEO.</p>
      </div>
    </BlocoRecolhivel>
  );
}
